import { Component, Host, h, Prop, Element, Watch, Build } from '@stencil/core';
import { Product } from '../../helpers/utils/interfaces';
import {
  renderArticleImg,
  renderFormattedArticleName,
  renderFormattedArticlePrice,
} from '../../helpers/helpers';
import Splide from '@splidejs/splide';

@Component({
  tag: 'cms-article-slider',
  styleUrl: 'cms-article-slider.scss',
  shadow: true,
})
export class CmsArticleSlider {
  private articles: Product[];

  private splideNode: HTMLElement;

  private splideObj: Splide;

  private defaultConf = {
    type: 'slide',
    perMove: 1,
    gap: '0.75rem',
    autoWidth: true,
    width: '1090px',
    slideFocus: true,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
  };

  /**
   * Set the Headline is optional
   */
  @Prop() headline: string;

  /**
   * set the Products of the Slider
   */
  @Prop() products!: Product[] | string;

  @Element() hostElement: HTMLElement;

  @Watch('products')
  private arrayProductsWatcher(newValue: Product[] | string) {
    if (typeof newValue === 'string') {
      this.articles = JSON.parse(newValue);
    }
  }

  private mapArticles() {
    return this.articles.map((article: Product) => {
      const {
        detailsUrl,
        name,
        image,
        energyEfficiencyClass,
        energyEfficiencyRank,
        energyEfficiencyLabelUrl,
        energyEfficiencyVersion,
        price,
        priceUnit,
        priceCurrency,
        bracketPrice,
        bracketPriceUnit,
      } = article;
      return (
        <li key={name} class={'splide__slide article-slider-list'}>
          <div class={'splide__slide__container'}>
            <a href={detailsUrl} class={'article-slider-cta'}>
              {this.renderArticleImageWrapper(name, image)}
              {this.renderArticleInfoWrapper(article)}
            </a>

            {energyEfficiencyClass != undefined &&
            energyEfficiencyRank != undefined &&
            energyEfficiencyVersion != undefined
              ? this.renderArticleEnergyLabel(
                  energyEfficiencyClass,
                  energyEfficiencyRank,
                  energyEfficiencyLabelUrl,
                  energyEfficiencyVersion,
                )
              : ''}
            {renderFormattedArticlePrice(
              price,
              priceUnit,
              priceCurrency,
              bracketPrice,
              bracketPriceUnit,
            )}
          </div>
        </li>
      );
    });
  }

  private renderArticleImageWrapper(name: string, image: string) {
    return (
      <div class={'article-image-wrapper'}>{renderArticleImg(name, image)}</div>
    );
  }
  private renderArticleRating(ratingValue: number, ratingCount: number) {
    return (
      <cms-rating
        ratingValue={ratingValue}
        ratingCount={ratingCount}
      ></cms-rating>
    );
  }

  private renderArticleEnergyLabel(
    energyEfficiencyClass: string,
    energyEfficiencyRank: string,
    energyEfficiencyLabelUrl: string,
    energyEfficiencyVersion: string,
  ) {
    return (
      <cms-energy-label
        energyLabel={energyEfficiencyClass}
        energyRank={energyEfficiencyRank}
        energyImgUrl={energyEfficiencyLabelUrl}
        energyVersion={energyEfficiencyVersion}
      ></cms-energy-label>
    );
  }

  private renderArticleInfoWrapper(article: Product) {
    const { name, ratingValue, ratingCount } = article;

    return (
      <div class={'article-info-wrapper'}>
        {name.length <= 40
          ? renderFormattedArticleName(name)
          : renderFormattedArticleName(this.shorterArticleName(name))}

        {this.renderArticleRating(ratingValue, ratingCount)}
      </div>
    ) as HTMLElement;
  }

  private shorterArticleName(name: string) {
    return `${name.substring(0, 40)} ...`;
  }

  private mountSplide() {
    if (!Build.isBrowser || this.products == undefined) {
      return;
    }
    this.splideObj = new Splide(this.splideNode, this.defaultConf);
    this.splideObj.mount();
    this.onVisible();
  }

  private onVisible() {
    this.splideObj.on('visible', (splide: Splide) => {
      if (splide.index === this.splideObj.length - 1) {
        this.splideObj.Components.Arrows.arrows.next.classList.add('disabled');
      } else {
        this.splideObj.Components.Arrows.arrows.next.classList.remove(
          'disabled',
        );
      }
    });
  }

  componentWillLoad() {
    if (this.products != undefined) {
      this.arrayProductsWatcher(this.products);
    }
  }

  componentDidLoad() {
    this.splideNode = this.hostElement.shadowRoot.querySelector('.splide');
    if (this.products != undefined) {
      this.mountSplide();
    }
  }

  render() {
    return (
      <Host class={'cms-article-slider'}>
        {this.headline ? (
          <div class={'headline-wrapper'}>
            <h2 class={'cms-article-slider-headline'}>{this.headline}</h2>
          </div>
        ) : null}
        <div class={'cms-article-slider-inner'}>
          <div class={'splide'}>
            <div class={'splide-inner'}>
              <div class='splide__arrows'>
                <button
                  type={'button'}
                  class={'splide__arrow splide__arrow--prev'}
                >
                  <cms-icon size='md' icon='arrowLeft'></cms-icon>
                </button>
                <button
                  type={'button'}
                  class='splide__arrow splide__arrow--next'
                >
                  <cms-icon size='md' icon='arrowRight'></cms-icon>
                </button>
              </div>

              <div class={'splide__track'}>
                <ul class={'splide__list'}>{this.mapArticles()}</ul>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
